import React, { useContext } from "react";
import { useSiteConfig } from "../context/SiteConfigContext";
import { Link } from "react-router-dom";
import {
  FaFacebook,
  FaInstagram,
  FaTwitter,
  FaYoutube,
  FaLinkedin,
  FaEnvelope,
} from "react-icons/fa";
import { CategoryContext } from "../context/CategoryContext";

const quickLinks = [
  { name: "Shop", to: "/shop" },
  { name: "Blogs", to: "/blog" },
  { name: "About Us", to: "/about-us" },
  { name: "Contact Us", to: "/contact-us" },
];

const supportLinks = [
  { name: "Cancellation Policy", to: "/cancellation-policy" },
  { name: "Terms & Conditions", to: "/terms-and-conditions" },
  { name: "Return & Refund Policy", to: "/return-and-refund-policy" },
];

// Static trust badges (UI only) - edit / remove as you like
const badges = [
  { label: "Secure", sublabel: "Payments" },
  { label: "Easy", sublabel: "Returns" },
  { label: "Fast", sublabel: "Shipping" },
  { label: "Quality", sublabel: "Assured" },
];

function Footer() {
  const { config } = useSiteConfig();
  const { categories } = useContext(CategoryContext);

  const socials = [
    { key: "facebook", Icon: FaFacebook },
    { key: "instagram", Icon: FaInstagram },
    { key: "twitter", Icon: FaTwitter },
    { key: "youtube", Icon: FaYoutube },
    { key: "linkedin", Icon: FaLinkedin },
  ];

  return (
    <>
      <style>{`
        .iv-footer {
          --f-primary:        var(--med-primary, #0A7CA5);
          --f-primary-dark:   var(--med-primary-dark, #086A8E);
          --f-primary-muted:  var(--med-primary-muted, #B3DCEA);
          --f-bg:             var(--med-text-primary, #1A2333);
          --f-text-inverse:   var(--med-text-inverse, #FFFFFF);
          --f-font:           var(--med-font-family, 'DM Sans', 'Segoe UI', system-ui, sans-serif);
          --f-font-xs:        var(--med-font-xs, 11px);
          --f-font-sm:        var(--med-font-sm, 12px);
          --f-font-md:        var(--med-font-md, 16px);
          --f-font-lg:        var(--med-font-lg, 18px);
          --f-semibold:       var(--med-font-semibold, 600);
          --f-bold:           var(--med-font-bold, 700);
          --f-lh-tight:       var(--med-line-height-tight, 1.3);
          --f-lh-base:        var(--med-line-height-base, 1.5);
          --f-radius-md:      var(--med-radius-md, 8px);
          --f-radius-pill:    var(--med-radius-pill, 999px);
          --f-fast:           var(--med-transition-fast, 150ms ease);

          font-family: var(--f-font);
          background: var(--f-bg);
          color: var(--f-text-inverse);
        }
        .iv-footer ul { list-style: none; padding: 0; margin: 0; }
        .iv-footer p { margin: 0; }

        /* ── CTA Banner ── */
        .iv-cta {
          background: var(--f-primary);
          padding: 40px 0;
        }
        .iv-cta-inner {
          display: flex;
          align-items: center;
          justify-content: space-between;
          gap: 24px;
          flex-wrap: wrap;
        }
        .iv-cta-text h3 {
          font-size: var(--f-font-lg);
          font-weight: var(--f-bold);
          color: #fff;
          margin: 0 0 4px;
          line-height: var(--f-lh-tight);
        }
        .iv-cta-text p {
          font-size: var(--f-font-sm);
          color: var(--f-primary-muted);
        }
        .iv-cta-actions {
          display: flex;
          gap: 10px;
          flex-shrink: 0;
          flex-wrap: wrap;
        }
        .iv-btn-primary,
        .iv-btn-outline {
          border-radius: var(--f-radius-pill);
          padding: 11px 26px;
          font-size: var(--f-font-sm);
          font-family: var(--f-font);
          cursor: pointer;
          text-decoration: none;
          display: inline-flex;
          align-items: center;
          gap: 6px;
        }
        .iv-btn-primary {
          background: #fff;
          color: var(--f-primary-dark);
          border: none;
          font-weight: var(--f-bold);
          transition: opacity var(--f-fast), transform var(--f-fast);
        }
        .iv-btn-primary:hover {
          opacity: 0.9;
          transform: translateY(-1px);
          color: var(--f-primary-dark);
        }
        .iv-btn-outline {
          background: transparent;
          color: #fff;
          border: 1px solid rgba(255,255,255,0.4);
          font-weight: var(--f-semibold);
          transition: background var(--f-fast), border-color var(--f-fast);
        }
        .iv-btn-outline:hover {
          background: rgba(255,255,255,0.1);
          border-color: rgba(255,255,255,0.7);
          color: #fff;
        }

        /* ── Main Body ── */
        .iv-body {
          padding: 64px 0 40px;
          border-bottom: 1px solid rgba(255,255,255,0.08);
        }
        .iv-brand-col { padding-right: 40px; }
        .iv-logo {
          height: 90px;
          width: auto;
          object-fit: contain;
          display: block;
          margin-bottom: 18px;
        }
        .iv-tagline {
          font-size: var(--f-font-sm);
          color: rgba(255,255,255,0.45);
          line-height: var(--f-lh-base);
          margin-bottom: 24px !important;
          max-width: 260px;
        }

        /* Badges */
        .iv-badge-grid {
          display: grid;
          grid-template-columns: repeat(4, 1fr);
          gap: 8px;
          margin-bottom: 28px;
          max-width: 280px;
        }
        .iv-badge {
          background: rgba(255,255,255,0.05);
          border: 1px solid rgba(255,255,255,0.1);
          border-radius: var(--f-radius-md);
          padding: 8px 6px;
          text-align: center;
        }
        .iv-badge-label {
          font-size: var(--f-font-xs);
          font-weight: var(--f-bold);
          color: var(--f-primary-muted);
          display: block;
          line-height: 1.2;
        }
        .iv-badge-sub {
          font-size: 10px;
          color: rgba(255,255,255,0.35);
          display: block;
          margin-top: 2px;
        }

        /* Social */
        .iv-social-row {
          display: flex;
          gap: 8px;
          flex-wrap: wrap;
        }
        .iv-social-btn {
          width: 34px;
          height: 34px;
          border-radius: var(--f-radius-md);
          background: rgba(255,255,255,0.07);
          border: 1px solid rgba(255,255,255,0.1);
          display: flex;
          align-items: center;
          justify-content: center;
          color: rgba(255,255,255,0.5);
          text-decoration: none;
          transition: background var(--f-fast), color var(--f-fast), border-color var(--f-fast);
        }
        .iv-social-btn:hover {
          background: var(--f-primary);
          border-color: var(--f-primary);
          color: #fff;
        }

        /* Link columns */
        .iv-col-heading {
          font-size: var(--f-font-xs);
          font-weight: var(--f-semibold);
          color: rgba(255,255,255,0.5);
          letter-spacing: 0.1em;
          text-transform: uppercase;
          margin-bottom: 18px !important;
        }
        .iv-link-list {
          display: flex;
          flex-direction: column;
          gap: 11px;
        }
        .iv-link-list a {
          font-size: var(--f-font-sm);
          color: rgba(255,255,255,0.55);
          text-decoration: none;
          display: inline-flex;
          align-items: center;
          gap: 6px;
          transition: color var(--f-fast);
        }
        .iv-link-list a:hover { color: #fff; }
        .iv-link-list a:hover .iv-link-arrow {
          opacity: 1;
          transform: translateX(2px);
        }
        .iv-link-arrow {
          opacity: 0;
          font-size: 10px;
          transition: opacity var(--f-fast), transform var(--f-fast);
        }

        /* Contact */
        .iv-contact-item {
          display: flex;
          align-items: center;
          gap: 10px;
          font-size: var(--f-font-sm);
          color: rgba(255,255,255,0.55);
          word-break: break-word;
        }
        .iv-contact-icon {
          color: var(--f-primary-muted);
          flex-shrink: 0;
        }

        /* ── Bottom Bar ── */
        .iv-bottom { padding: 24px 0; }
        .iv-bottom-inner {
          display: flex;
          align-items: center;
          justify-content: space-between;
          gap: 16px;
          flex-wrap: wrap;
        }
        .iv-copy {
          font-size: var(--f-font-xs);
          color: rgba(255,255,255,0.3);
        }
        .iv-legal-links {
          display: flex;
          align-items: center;
          gap: 4px;
          flex-wrap: wrap;
        }
        .iv-legal-link {
          font-size: var(--f-font-xs);
          color: rgba(255,255,255,0.35);
          text-decoration: none;
          padding: 2px 8px;
          border-radius: var(--f-radius-pill);
          transition: color var(--f-fast), background var(--f-fast);
        }
        .iv-legal-link:hover {
          color: #fff;
          background: rgba(255,255,255,0.07);
        }
        .iv-legal-sep {
          color: rgba(255,255,255,0.12);
          font-size: 10px;
          user-select: none;
        }
        .iv-designer {
          display: inline-flex;
          align-items: center;
        }
        .iv-designer img {
          height: 22px;
          width: auto;
          opacity: 0.7;
          transition: opacity var(--f-fast);
        }
        .iv-designer:hover img { opacity: 1; }

        /* ── Responsive ── */
        @media (max-width: 991px) {
          .iv-brand-col { padding-right: 0; margin-bottom: 40px; }
          .iv-cta-inner { flex-direction: column; text-align: center; }
          .iv-cta-actions { justify-content: center; }
        }
        @media (max-width: 767px) {
          .iv-body { padding: 48px 0 32px; }
          .iv-bottom-inner {
            flex-direction: column;
            align-items: flex-start;
            gap: 12px;
          }
        }
        @media (max-width: 575px) {
          .iv-cta { padding: 28px 0; }
          .iv-cta-text h3 { font-size: var(--f-font-md); }
        }
      `}</style>

      <footer className="iv-footer">
        {/* ── CTA Banner ── */}
        <div className="iv-cta">
          <div className="container">
            <div className="iv-cta-inner">
              <div className="iv-cta-text">
                <h3>Explore our medical equipment</h3>
                <p>Discover reliable medical equipment and healthcare solutions designed to meet the needs of healthcare professionals.</p>
              </div>
              <div className="iv-cta-actions">
                <Link to="/shop" className="iv-btn-primary">
                  Shop Now
                </Link>
                <Link to="/contact-us" className="iv-btn-outline">
                  Contact Us
                </Link>
              </div>
            </div>
          </div>
        </div>

        {/* ── Main Body ── */}
        <div className="iv-body">
          <div className="container">
            <div className="row">
              {/* Brand Column */}
              <div className="col-lg-4 col-md-12">
                <div className="iv-brand-col">
                  {/* <img src={config?.logo} alt="logo" className="iv-logo" /> */}
                  <img 
                    src={config?.logo || "https://theorangeapes.com/frontend/img/logo.webp"} 
                    alt="Brand Logo" 
                    className="iv-logo"
                  />
                  <p className="iv-tagline">
                   Advancing Healthcare with Innovation. Discover our comprehensive range of medical equipment and healthcare solutions, designed to support medical professionals, enhance patient care, and build a healthier future.
                  </p>

                  <div className="iv-badge-grid">
                    {badges.map((b) => (
                      <div className="iv-badge" key={b.label}>
                        <span className="iv-badge-label">{b.label}</span>
                        <span className="iv-badge-sub">{b.sublabel}</span>
                      </div>
                    ))}
                  </div>

                  <div className="iv-social-row">
                    {socials.map(
                      ({ key, Icon }) =>
                        config?.socialMedia?.[key] && (
                          <a
                            key={key}
                            href={config.socialMedia[key]}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="iv-social-btn"
                            aria-label={key}
                          >
                            <Icon size={15} />
                          </a>
                        )
                    )}
                  </div>
                </div>
              </div>

              {/* Quick Links */}
              <div className="col-lg-2 col-sm-6 col-6 mt-4 mt-lg-0">
                <p className="iv-col-heading">Quick Links</p>
                <ul className="iv-link-list">
                  {quickLinks.map((l) => (
                    <li key={l.to}>
                      <Link to={l.to}>
                        {l.name}
                        <span className="iv-link-arrow">›</span>
                      </Link>
                    </li>
                  ))}
                </ul>
              </div>

              {/* Support */}
              <div className="col-lg-3 col-sm-6 col-6 mt-4 mt-lg-0">
                <p className="iv-col-heading">Support</p>
                <ul className="iv-link-list">
                  {supportLinks.map((l) => (
                    <li key={l.to}>
                      <Link to={l.to}>
                        {l.name}
                        <span className="iv-link-arrow">›</span>
                      </Link>
                    </li>
                  ))}
                </ul>
              </div>

              {/* Contact */}
              <div className="col-lg-3 col-sm-12 col-12 mt-4 mt-lg-0">
                <p className="iv-col-heading">Stay Updated</p>
                <div className="iv-contact-item">
                  <FaEnvelope size={14} className="iv-contact-icon" />
                  <span>{config?.email}</span>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* ── Bottom Bar ── */}
        <div className="iv-bottom">
          <div className="container">
            <div className="iv-bottom-inner">
              <p className="iv-copy">All Rights Reserved © 2026 Disha Inc.</p>

              <div className="iv-legal-links">
                {supportLinks.map((l, i) => (
                  <span
                    key={l.to}
                    style={{ display: "flex", alignItems: "center" }}
                  >
                    <Link to={l.to} className="iv-legal-link">
                      {l.name}
                    </Link>
                    {i < supportLinks.length - 1 && (
                      <span className="iv-legal-sep">·</span>
                    )}
                  </span>
                ))}
              </div>

              <div className="col-md-6 text-md-end d-flex justify-content-center justify-content-md-end">
             <a
                  href="https://axesell.com"
                  className="floating-designer-btn"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  <div className="floating-content">
                    <img
                      src={`${process.env.PUBLIC_URL}/images/axesell_logo.png`}
                      className="floating-icon"
                      alt="Axesell"
                    />
                  </div>
                </a>
          </div>
            </div>
          </div>
        </div>
      </footer>
    </>
  );
}

export default Footer;